/*
 * Copyright (c) 2019 Frontier Developments PLC.
 */

import Axios, {
    AxiosAdapter,
    AxiosError,
    AxiosInstance,
    AxiosInterceptorManager, AxiosPromise,
    AxiosRequestConfig,
    AxiosResponse,
} from 'axios';
import ApiException from '@/exceptions/ApiException';
import axiosRetry from 'axios-retry';
import {cacheAdapterEnhancer, throttleAdapterEnhancer} from 'axios-extensions';
import {HttpConfigurationInterface} from '@/services/http/types';
import CorsException from '@/exceptions/CorsException';

/**
 * Api service class used to leverage axios http library.
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export abstract class AbstractHttpService {
    // mapped axios methods
    public request: <T = any>(config: AxiosRequestConfig) => AxiosPromise<T>;
    public get: <T = any>(url: string, config?: AxiosRequestConfig) => AxiosPromise<T>;
    public delete: (url: string, config?: AxiosRequestConfig) => AxiosPromise;
    public head: (url: string, config?: AxiosRequestConfig) => AxiosPromise;
    public post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => AxiosPromise<T>;
    public put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => AxiosPromise<T>;
    public patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => AxiosPromise<T>;

    // tslint:disable-next-line:variable-name
    private readonly _axios: AxiosInstance;

    /**
     *  Constructor.
     *
     *  This includes the configuration of the following plugins:
     *  1. Throttle adapter - ensures that a route is not flooded with requests during a pre-defined time period.
     *  2. Cache adapter - caches requests. Important - this should be deactivated on some routes: {cache: false}.
     *  3. Retry plugin - attempts retries on routes that fail.
     */
    protected constructor(public configuration: HttpConfigurationInterface) {
        // setup up config
        const config: AxiosRequestConfig = {
            baseURL: configuration.baseURL,
        };

        // configure cache adapter
        if (configuration.cache) {
            config.adapter = cacheAdapterEnhancer(
                Axios.defaults.adapter as AxiosAdapter,
                {
                    enabledByDefault: !!configuration.cache.enabledByDefault,
                    cacheFlag: configuration.cache.cacheFlag || 'cache',
                },
            );
        }

        // configure throttle adapter
        if (configuration.throttle) {
            config.adapter = throttleAdapterEnhancer(
                !config.adapter ? Axios.defaults.adapter as AxiosAdapter : config.adapter,
                { threshold: configuration.throttle.threshold },
            );
        }

        // create axios client
        this._axios = Axios.create(config);
        this.get = this._axios.get;
        this.delete = this._axios.delete;
        this.post = this._axios.post;
        this.put = this._axios.put;
        this.request = this._axios.request;
        this.patch = this._axios.patch;
        this.head = this._axios.head;

        // configure the axios retry plugin
        if (configuration.retry) {
            axiosRetry(this._axios, {
                retries: configuration.retry.maximum || 1,
                retryCondition: configuration.retry.condition || ((): boolean => true),
                retryDelay: (count: number): number => count * (configuration.retry ? configuration.retry.period : 1),
            });
        }

        // add content type interceptor
        this._axios.interceptors.request.use((setup: AxiosRequestConfig): AxiosRequestConfig => {
            if (!setup.headers['content-type'] && configuration.contentType) {
                setup.headers['content-type'] = configuration.contentType;
            }

            if (!setup.headers['Accept'] && configuration.accept) {
                setup.headers['Accept'] = configuration.accept;
            }

            return setup;
        });

        // add response interceptor
        this.addResponseInterceptors(this._axios.interceptors.response);

        // add request interceptor
        this.addRequestInterceptors(this._axios.interceptors.request);
    }

    /**
     * Add request interceptors.
     *
     * This method can be overridden in inheriting classes - as can the default request interceptors that it registers.
     *
     * @param {AxiosInterceptorManager<AxiosRequestConfig>} request
     */
    public addRequestInterceptors(request: AxiosInterceptorManager<AxiosRequestConfig>): void {
        // do nothing here
    }

    /**
     * Add response interceptors.
     *
     * This method can be overridden in inheriting classes - as can the default response interceptors that it registers.
     *
     * @param {AxiosInterceptorManager<AxiosResponse>} response
     */
    public addResponseInterceptors(response: AxiosInterceptorManager<AxiosResponse>): void {
        response.use(
            (axiosResponse: AxiosResponse) => this.handleSuccess(axiosResponse),
            (axiosError: AxiosError): Promise<any> => this.handleError(axiosError),
        );
    }

    /**
     * Get axios client.
     *
     * @return {AxiosInstance}
     */
    get axios(): AxiosInstance {
        return this._axios;
    }

    /**
     * Global success response handler
     *
     * @param {AxiosResponse} response
     * @returns {AxiosResponse}
     */
    public handleSuccess(response: AxiosResponse): AxiosResponse {
        // Custom response handling
        return response;
    }

    /**
     * Inheritable exception handler.
     *
     * @param {ApiException} exception
     * @return {Promise<ApiException>}
     */
    public handleException(exception: ApiException): Promise<never> {
        // Custom error handling
        return Promise.reject(exception);
    }

    /**
     * Global error response handler
     * @param error
     * @returns {Promise<any>}
     */
    public async handleError(error: AxiosError): Promise<any> {
        // typically if there is an undefined response then it is a CORS issue
        if (undefined === error.response) {
            return Promise.reject(new CorsException(error));
        }

        if (!error.response) {
            return Promise.reject(error);
        }

        const exception: ApiException = new ApiException(
            error.response.status,
            error.response.data,
        );

        // pass through to built in exception handler
        return this.handleException(exception);
    }
}
